import {
  DigestJobDetail,
  DigestJobResult,
  ReportFeedbackChartDataRequestItem,
  ReportChartGroupCompareItemType,
  ReportChartJobIdResponse,
  ReportChartMeasureType,
  ReportFeedbackData,
  ReportFeedbackRequest,
} from '@vision/ui/interfaces';

type CachedChartResponseBase = {
  compare?: ReportChartGroupCompareItemType[];
  measure: ReportChartMeasureType;
};

export type CachedChartResponse =
  | (CachedChartResponseBase & {
      data: ReportFeedbackData;
      status: 'result';
    })
  | (CachedChartResponseBase & {
      data: ReportChartJobIdResponse;
      status: 'job';
    })
  | (CachedChartResponseBase & {
      status: 'missing';
    });

type CachedJobDetailWithLegacyParams = DigestJobDetail & {
  params?: Partial<ReportFeedbackRequest>;
  request?: Partial<ReportFeedbackRequest>;
};

const CACHED_ACTION_ALIASES: Record<string, string[]> = {
  funnel: ['campaign_funnel', 'funnel_campaign'],
  matrix: ['response_matrix'],
  metric: ['action_metric', 'action_metrics'],
};

const IGNORED_SIGNATURE_KEYS = new Set(['async', 'dashboard_widget_id', 'digest', 'key', 'current_user_id']);

function getCachedActionCandidates(url: string): string[] {
  const action = url.split('/').pop();

  if (!action) {
    return [];
  }

  return [action, ...(CACHED_ACTION_ALIASES[action] ?? [])];
}

function normalizeSignatureValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizeSignatureValue);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        if (IGNORED_SIGNATURE_KEYS.has(key)) {
          return acc;
        }

        const nextValue = (value as Record<string, unknown>)[key];
        if (nextValue === undefined || nextValue === null || nextValue === '') {
          return acc;
        }

        acc[key] = normalizeSignatureValue(nextValue);
        return acc;
      }, {});
  }

  return value;
}

export function getCachedRequestSignature({
  compare,
  data,
  measure,
}: Pick<ReportFeedbackChartDataRequestItem, 'compare' | 'data' | 'measure'>): string {
  return JSON.stringify(
    normalizeSignatureValue({
      compare,
      data,
      measure,
    }),
  );
}

function cachedDetailHasResult(detail: DigestJobDetail): detail is DigestJobDetail & { result: ReportFeedbackData } {
  return Object.prototype.hasOwnProperty.call(detail, 'result');
}

function getDetailRequestParams(detail: DigestJobDetail): Partial<ReportFeedbackRequest> | undefined {
  const detailWithParams = detail as CachedJobDetailWithLegacyParams;

  return detailWithParams.request_params || detailWithParams.params || detailWithParams.request;
}

function detailMatchesRequestSignature(
  detail: DigestJobDetail,
  request: Pick<ReportFeedbackChartDataRequestItem, 'compare' | 'data' | 'measure'>,
): boolean {
  if (detail.request_signature) {
    return detail.request_signature === getCachedRequestSignature(request);
  }

  const detailRequestParams = getDetailRequestParams(detail);
  if (!detailRequestParams) {
    return false;
  }

  return (
    getCachedRequestSignature({
      compare: request.compare,
      data: detailRequestParams,
      measure: request.measure,
    }) === getCachedRequestSignature(request)
  );
}

export function findCachedJobDetail({
  allowLegacyFallback = true,
  details,
  request,
  url,
}: {
  allowLegacyFallback?: boolean;
  details: DigestJobDetail[];
  request: Pick<ReportFeedbackChartDataRequestItem, 'compare' | 'data' | 'measure'>;
  url: string;
}): DigestJobDetail | undefined {
  const actions = getCachedActionCandidates(url);
  const actionMatches = details.filter((detail) => actions.includes(detail.action));

  const signatureMatch = actionMatches.find((detail) => detailMatchesRequestSignature(detail, request));
  if (signatureMatch) {
    return signatureMatch;
  }

  if (!allowLegacyFallback && actionMatches.length > 1) {
    return undefined;
  }

  return actionMatches[0];
}

export function getCachedChartResponse({
  cachedResults,
  compare,
  data,
  measure,
  requireResult,
  url,
  widgetId,
}: {
  cachedResults?: DigestJobResult[] | null;
  compare?: ReportChartGroupCompareItemType[];
  data: ReportFeedbackRequest;
  measure: ReportChartMeasureType;
  requireResult?: boolean;
  url: string;
  widgetId?: string;
}): CachedChartResponse {
  const element = cachedResults?.find((element) => element.widget_id === widgetId);
  const detail = element
    ? findCachedJobDetail({
        allowLegacyFallback: !requireResult,
        details: element.details,
        request: { compare, data, measure },
        url,
      })
    : undefined;

  if (!detail) {
    return { compare, measure, status: 'missing' };
  }

  if (detail.status === 'completed' && cachedDetailHasResult(detail)) {
    return { compare, data: detail.result, measure, status: 'result' };
  }

  if (detail.status === 'completed' && !requireResult) {
    return { compare, data: { job_id: detail.job_id }, measure, status: 'job' };
  }

  return { compare, measure, status: 'missing' };
}
